import { Injectable, signal } from '@angular/core';
import { LoginCampaignResponse, LoginResponseUser } from './models/login.model';

const ACCESS_TOKEN_KEY = 'incentivos_access_token';
const USER_KEY = 'incentivos_user';
const EXPIRES_AT_KEY = 'incentivos_token_expires_at';
const USERNAME_KEY = 'incentivos_username';
const CAMPAIGN_KEY = 'incentivos_campaign';

function readStoredUser(): LoginResponseUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as LoginResponseUser;
  } catch {
    return null;
  }
}
function readStoredCampaign(): LoginCampaignResponse | null {
  const raw = localStorage.getItem(CAMPAIGN_KEY);

  if (!raw) return null;

  try {
    return JSON.parse(raw) as LoginCampaignResponse;
  } catch {
    return null;
  }
}

@Injectable({ providedIn: 'root' })
export class AuthTokenService {
  private readonly tokenSignal = signal<string | null>(localStorage.getItem(ACCESS_TOKEN_KEY));
  private readonly userSignal = signal<LoginResponseUser | null>(readStoredUser());
  private readonly expiresAtSignal = signal<string | null>(localStorage.getItem(EXPIRES_AT_KEY));
  private readonly usernameSignal = signal<string | null>(localStorage.getItem(USERNAME_KEY));
  private readonly campaignSignal = signal<LoginCampaignResponse | null>(readStoredCampaign());

  readonly token = this.tokenSignal.asReadonly();
  readonly user = this.userSignal.asReadonly();
  readonly expiresAt = this.expiresAtSignal.asReadonly();
  readonly username = this.usernameSignal.asReadonly();
  readonly campaign = this.campaignSignal.asReadonly();

  getToken(): string | null {
    return this.tokenSignal();
  }

  setToken(token: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    this.tokenSignal.set(token);
  }

  setUser(user: LoginResponseUser): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    this.userSignal.set(user);
  }

  setExpiresAt(expiresAtUtc: string): void {
    localStorage.setItem(EXPIRES_AT_KEY, expiresAtUtc);
    this.expiresAtSignal.set(expiresAtUtc);
  }

  setUsername(username: string): void {
    localStorage.setItem(USERNAME_KEY, username);
    this.usernameSignal.set(username);
  }

  setCampaign(campaign: LoginCampaignResponse): void {
    localStorage.setItem(CAMPAIGN_KEY, JSON.stringify(campaign));
    this.campaignSignal.set(campaign);
  }

  clearToken(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(EXPIRES_AT_KEY);
    localStorage.removeItem(USERNAME_KEY);
    localStorage.removeItem(CAMPAIGN_KEY);
    this.tokenSignal.set(null);
    this.userSignal.set(null);
    this.expiresAtSignal.set(null);
    this.usernameSignal.set(null);
    this.campaignSignal.set(null);
  }
}
